import React, { useCallback, useEffect, useState } from "react";
import { useMediaQuery } from "@mui/system";
import {
  ArrayField,
  Datagrid,
  FunctionField,
  ReferenceField,
  SimpleShowLayout,
  TextField,
  useRecordContext,
} from "react-admin";
import { Grid } from "@mui/material";
import { settings } from "app";
import dataProvider from "server/dataProvider";
import SecuredComponent from "server/security/SecuredComponent";
import permissions from "app/permissions";
import AddSportScheduleDialog from "./AddSportScheduleDialog";
import { WeekDaysChoices, WeekTypeChoices } from "utils/choices";
import RoomField from "server/components/Fields/RoomField";
import ProfessorField from "server/components/Fields/ProfessorField";
import DeleteSportScheduleDialog from "./DeleteSportScheduleDialog";
import { activityRequests } from "api/activityApi";
import { studentRequests } from "api/studentApi";

const SportActivitiesData = ({ educationId }) => {
  const record = useRecordContext();

  const isMobile = useMediaQuery(theme => theme.breakpoints.down("md"));
  const [sportActivities, setSportActivities] = useState([]);

  const fetchSportActivities = useCallback(async () => {
    if (!educationId) return;
    try {
      const { data } = await dataProvider.customRequest(
        activityRequests.studentSportActivities(educationId),
        null,
        "GET",
      );
      setSportActivities(data);
    } catch (err) {
      console.error(err.message);
    }
  }, [educationId]);

  useEffect(() => {
    fetchSportActivities();
  }, [fetchSportActivities]);

  return (
    <SimpleShowLayout record={{ sportActivities }}>
      <Grid
        container
        sx={isMobile ? settings.style.shadowBoxMobile : settings.style.shadowBox}
      >
        <Grid
          item
          xs={12}
        >
          <ArrayField source="sportActivities">
            <Datagrid
              rowClick={false}
              bulkActionButtons={false}
              sx={{ margin: 2, ...settings.style.secondaryDatagrid }}
            >
              <TextField
                source="academicYearName"
                label="Учебна година"
              />
              <TextField
                source="sportActivity.activityNameBg"
                label="Спорт"
              />
              <ReferenceField
                source="sportActivity.academicSessionId"
                resource={studentRequests.getAcademicSessionsSelect()}
                reference={studentRequests.getAcademicSessionsSelect()}
                label="Семестър"
              >
                <TextField source="sessionTypeText" />
              </ReferenceField>
              <FunctionField
                label="Курс"
                render={record =>
                  record.sportActivity.course === 1
                    ? "1-ви Курс"
                    : record.sportActivity.course === 2
                      ? "2-ри Курс"
                      : record.sportActivity.course === 3
                        ? "3-ти Курс"
                        : record.sportActivity.course === 4
                          ? "4-ти Курс"
                          : record.sportActivity.course === 5
                            ? "5-ти Курс"
                            : record.sportActivity.course === 6
                              ? "6-ти Курс"
                              : "-"
                }
              />
              <FunctionField
                label="Тип седмица"
                render={record => WeekTypeChoices.find(d => d.id === record.sportActivity.weekType)?.name}
              />
              <FunctionField
                label="Ден"
                render={record => WeekDaysChoices.find(d => d.id === record.sportActivity.weekDay)?.name}
              />
              <FunctionField
                label="Час"
                render={record =>
                  `${record.sportActivity.startTime.slice(0, 5)} - ${record.sportActivity.endTime.slice(0, 5)}`
                }
              />
              <RoomField
                source="sportActivity.roomId"
                label="Зала"
              />
              <ProfessorField
                source="sportActivity.professorEmployeeId"
                label="Преподавател"
              />
              <SecuredComponent permission={permissions.canManageStudentsSportActivities}>
                <DeleteSportScheduleDialog
                  educationId={educationId}
                  fetchSportActivities={fetchSportActivities}
                />
              </SecuredComponent>
            </Datagrid>
          </ArrayField>
        </Grid>
        <Grid
          item
          xs={12}
          sx={{ ml: 2 }}
        >
          <SecuredComponent permission={permissions.canManageStudentsSportActivities}>
            <AddSportScheduleDialog
              educationId={educationId}
              curriculumId={record?.curriculumId}
              instructionLanguageId={record?.instructionLanguageId}
              studyTypeId={record?.studyTypeId}
              sportActivities={sportActivities}
              fetchSportActivities={fetchSportActivities}
            />
          </SecuredComponent>
        </Grid>
      </Grid>
    </SimpleShowLayout>
  );
};

export default SportActivitiesData;
